import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons'
import {
  Flex,
  Box,
  Text,
  LoadingOverlay,
  Title,
  Switch,
  Card,
  Tooltip,
} from '@mantine/core'

import { useQueryGetAllBookingInsurances } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type ParameterID } from '@flecto/types'
import { Icon, iconInsuranceOn, TextCurrency } from '@flecto/ui'

import {
  TableBookingInsurances,
  useBookingInsuranceToggleStatus,
} from '../../../booking-insurances'
import classes from './styles.module.css'

type InsuranceMobileTabProps = {
  bookingId: ParameterID
}

export function InsuranceMobileTab({ bookingId }: InsuranceMobileTabProps) {
  const {
    toggleBookingInsuranceStatus,
    isToggling,
    isBookingInsuranceEnabled,
  } = useBookingInsuranceToggleStatus({ bookingId })

  const { data: insurances } = useQueryGetAllBookingInsurances({
    bookingId,
  })

  const { t } = useTranslation('saas', { keyPrefix: 'booking_sidebar' })

  const totalCents = insurances?.total_cents ?? 0

  const { data: bookingInsurances, isLoading: isLoadingInsurances } =
    useQueryGetAllBookingInsurances({ bookingId })

  return (
    <Box mt='lg' className={classes.card}>
      <Card>
        <LoadingOverlay visible={isToggling} />
        <Flex justify='space-between' align='center'>
          <Flex gap={8}>
            <Icon icon={iconInsuranceOn} color='brand' />
            <Text>{t('insurances_modal.active_insurance.title_small')}</Text>
          </Flex>
          <Switch
            checked={isBookingInsuranceEnabled}
            onChange={toggleBookingInsuranceStatus}
          />
        </Flex>
        <Box mt='sm'>
          <Text>{t('insurances_modal.active_insurance.description')}</Text>
        </Box>
      </Card>

      <Box mt='lg'>
        <Title order={6}>{t('insurances_modal.issues.title')}</Title>
        <TableBookingInsurances
          mt='md'
          insurances={bookingInsurances?.insurance ?? []}
          isLoading={isLoadingInsurances}
        />

        <Flex className={classes.totals} mt='xl' gap={8} direction='column'>
          <Flex justify='space-between'>
            <Flex>
              <Text>{t('insurances_modal.total_cost.title')}</Text>
            </Flex>
            <TextCurrency cents={totalCents} />
          </Flex>
          <Flex justify='space-between'>
            <Flex align='center' gap='xs'>
              <Text>{t('insurances_modal.total_cost.base_cost')}</Text>
              <Tooltip label={t('insurances_modal.tooltips.base_fee_tooltip')}>
                <Box>
                  <Icon icon={faQuestionCircle} />
                </Box>
              </Tooltip>
            </Flex>
            <TextCurrency cents={totalCents} />
          </Flex>
          <Flex justify='space-between'>
            <Text>Total</Text>
            <TextCurrency cents={totalCents} />
          </Flex>
        </Flex>
      </Box>
    </Box>
  )
}
